import { Screens, screenToResource } from '~/constants/config/permissions';
import type { AuthUser } from '~/store/auth.store';

/**
 * Single source of truth for role- and permission-based access decisions,
 * shared by the route `beforeLoad` guards (`~/utils/guards`) and the sidebar
 * visibility logic (`copilot-shell`). Keeping both on the same predicates is
 * what prevents the "menu shows it, route denies it" divergence: if a user
 * can't reach a page, the menu entry for it is never rendered.
 *
 * Pure module — no hooks, no redirects. Guards wrap these in `redirect(...)`;
 * components read `user` from the store and call them directly.
 */

/** Role codes that grant access to org-level settings pages. Must stay aligned
 *  with identity-service `ADMIN_ROLE_CODES` — `SUBSIDIARY_ADMIN` is the UAT
 *  rename of `ADMINISTRATOR` (same admin level, treated as an alias backend-side). */
export const TENANT_ADMIN_ROLES = ['OWNER', 'ADMIN', 'ADMINISTRATOR', 'SUBSIDIARY_ADMIN'] as const;

/** True when the user holds a tenant-admin role. Super admins qualify too — they
 *  bypass every tenant-scoped check. */
export function isTenantAdmin(user: AuthUser | null | undefined): boolean {
  if (!user) return false;
  if (user.isSuperAdmin) return true;
  const upperRole = user.roleCode.toUpperCase();
  return TENANT_ADMIN_ROLES.includes(upperRole as (typeof TENANT_ADMIN_ROLES)[number]);
}

/** True when the user holds a tenant-admin role in one specific tenant (from
 *  `accessibleTenants`). Mirrors identity's per-subsidiary edit check, so no
 *  super-admin bypass: that only applies inside the system subsidiary. */
export function isTenantAdminOf(user: AuthUser | null | undefined, tenantId: string): boolean {
  const roleCode = user?.accessibleTenants.find((t) => t.id === tenantId)?.roleCode;
  if (!roleCode) return false;
  return TENANT_ADMIN_ROLES.includes(roleCode.toUpperCase() as (typeof TENANT_ADMIN_ROLES)[number]);
}

/** True when the user may access a screen: super-admin and tenant-admin bypass,
 *  else a direct permission match, else the screen's `resource:read` (the minimum
 *  for page access). Mirrors the `requirePermission` guard exactly. */
export function hasScreenAccess(user: AuthUser | null | undefined, screen: string): boolean {
  if (!user) return false;
  if (isTenantAdmin(user)) return true;
  if (user.permissions.includes(screen)) return true;
  const resource = screenToResource[screen];
  return !!resource && user.permissions.includes(`${resource}:read`);
}

/**
 * True for a Lily AI platform admin: a super admin whose ACTIVE subsidiary is the
 * system subsidiary — identity-service's `SYSTEM_ADMIN` rule. Taxonomy Studio is
 * gated on this (agentic-config answers 403 NOT_PLATFORM_ADMIN otherwise).
 */
export function isSystemSubsidiaryAdmin(user: AuthUser | null | undefined): boolean {
  if (!user?.isSuperAdmin) return false;
  const active = user.accessibleTenants.find((t) => t.id === user.activeTenantId);
  return active?.isSystem ?? false;
}

/** Who may reach a nav path. `superAdmin` = `isSuperAdmin` only; `tenantAdmin` =
 *  super- or tenant-admin; `permission` = the `requirePermission` semantics for a
 *  screen code. */
type NavAccessRule =
  | { kind: 'superAdmin' }
  | { kind: 'systemSubsidiaryAdmin' }
  | { kind: 'tenantAdmin' }
  | { kind: 'permission'; screen: string };

/**
 * Nav-path → access-rule policy. Each entry mirrors the matching route's
 * `beforeLoad` guard, so the sidebar hides exactly what the route would deny:
 *  - `/platform`, `/explorer`, `/integrations`, `/workflow-runs` → `requireSystemAdmin`
 *  - `/taxonomy`, `/schedules`, `/coverage` → `requireSystemSubsidiaryAdmin` (super admin in the system subsidiary)
 *  - `/subsidiary`, `/deliveries` → `requireTenantAdmin`
 *  - `/configuration` → `requirePermission(CONFIGURATION)`
 *  - `/content` → `requirePermission(CONTENT_GENERATION)`
 *  - `/integrations/sharing` → `requirePermission(INTEGRATIONS_SHARING)`
 * A path not listed here carries no role/permission restriction (auth-only) and
 * is governed solely by the sidebar's feature-flag / scope / trial checks.
 * When you add a guarded admin route, add its path here in the same commit.
 */
const NAV_ACCESS_RULES: Record<string, NavAccessRule> = {
  '/platform': { kind: 'superAdmin' },
  '/explorer': { kind: 'superAdmin' },
  '/integrations': { kind: 'superAdmin' },
  '/workflow-runs': { kind: 'superAdmin' },
  '/taxonomy': { kind: 'systemSubsidiaryAdmin' },
  '/schedules': { kind: 'systemSubsidiaryAdmin' },
  '/coverage': { kind: 'systemSubsidiaryAdmin' },
  '/subsidiary': { kind: 'tenantAdmin' },
  '/deliveries': { kind: 'tenantAdmin' },
  '/configuration': { kind: 'permission', screen: Screens.CONFIGURATION },
  '/content': { kind: 'permission', screen: Screens.CONTENT_GENERATION },
  '/integrations/sharing': { kind: 'permission', screen: Screens.INTEGRATIONS_SHARING },
};

/** True when the user is allowed to see/reach a nav path per `NAV_ACCESS_RULES`.
 *  Unlisted paths return true (no role/permission gate). The sidebar calls this
 *  so an item is only shown when the user could actually open it. */
export function canAccessNavPath(path: string, user: AuthUser | null | undefined): boolean {
  const rule = NAV_ACCESS_RULES[path];
  if (!rule) return true;
  switch (rule.kind) {
    case 'superAdmin':
      return user?.isSuperAdmin ?? false;
    case 'systemSubsidiaryAdmin':
      return isSystemSubsidiaryAdmin(user);
    case 'tenantAdmin':
      return isTenantAdmin(user);
    case 'permission':
      return hasScreenAccess(user, rule.screen);
  }
}
